import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { t } from '../../../i18n'
import { ChevronIcon, CleanEnterIcon, CleanExitIcon, QualityIcon, SpeedIcon } from './icons'

interface Option {
  value: number
  label: string
}

interface Props {
  rate: number
  rates: Option[]
  onPickRate: (rate: number) => void
  /** 拿不到清晰度档位时（iOS 原生 HLS、只有一档）不传，这一行就不出现。 */
  qualities?: Option[]
  quality: number
  onPickQuality: (shortSide: number) => void
  clean: boolean
  onToggleClean: () => void
  onClose: () => void
}

/**
 * 视频设置面板（长按中间、点「更多」或点顶栏倍速弹出），照红果 7.3.9 的 ShortSeriesMorePanelDialogV2，和 App 一样：
 * #FAFAFA 底、上两角 16 圆角、顶部 36×4 拖动条；白色 8 圆角的分组卡片，左右 16、组间 12；
 * 倍速、清晰度是「图标 + 文字 + 右侧分段选择」的选项行，清屏播放是 54 高的普通行（右边箭头）；最下面 0.5 分割线和「取消」。
 * 我们只取播放相关的三项——举报、不感兴趣这些面向用户投稿内容，我们的片子全是自己上架的。
 * 选了倍速、清晰度或点了清屏，面板就收起（调用方在回调里关）。
 *
 * 挂到 body 上、从屏幕底部弹出盖住选集条；留在播放器里的话，
 * 翻页容器的 transform 会把 fixed 定位困在那一屏里，面板只能停在选集条上面。
 */
export function QuickMenu({
  rate,
  rates,
  onPickRate,
  qualities,
  quality,
  onPickQuality,
  clean,
  onToggleClean,
  onClose,
}: Props) {
  return createPortal(
    <div style={styles.backdrop} onClick={onClose} role="presentation">
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal>
        <div style={styles.handle} aria-hidden />
        <div style={styles.cards}>
          <div style={styles.card}>
            <OptionRow icon={<SpeedIcon />} title={t.rate.title} options={rates} current={rate} onPick={onPickRate} />
            {qualities && (
              <OptionRow
                icon={<QualityIcon />}
                title={t.quality.title}
                options={qualities}
                current={quality}
                onPick={onPickQuality}
              />
            )}
          </div>
          <div style={styles.card}>
            <button style={styles.actionRow} onClick={onToggleClean}>
              {clean ? <CleanExitIcon /> : <CleanEnterIcon />}
              <span style={styles.label}>{clean ? t.cleanScreen.exit : t.cleanScreen.enter}</span>
              <span style={styles.chevron}>
                <ChevronIcon size={16} />
              </span>
            </button>
          </div>
        </div>
        <button style={styles.cancel} onClick={onClose}>
          {t.cancel}
        </button>
      </div>
    </div>,
    document.body,
  )
}

/** 选项行：上下 10，右边分段选择器离文字至少 28、离卡片右边 12。档位多、屏幕窄放不下时选择器自己横向滑 */
function OptionRow({
  icon,
  title,
  options,
  current,
  onPick,
}: {
  icon: ReactNode
  title: string
  options: Option[]
  current: number
  onPick: (value: number) => void
}) {
  return (
    <div style={styles.optionRow}>
      {icon}
      <span style={styles.label}>{title}</span>
      <div style={styles.scroller}>
        <Segmented options={options} current={current} onPick={onPick} />
      </div>
    </div>
  )
}

/**
 * 红果 ScrollableMultipleOptionsView：34 高、8 圆角、3% 黑的轨道，内边距 2；每格至少 37 宽、12 的字；
 * 选中格是白色 6 圆角的块，字加粗纯黑，其余 40% 黑；换选中时白块滑过去 300ms。
 */
function Segmented({ options, current, onPick }: { options: Option[]; current: number; onPick: (v: number) => void }) {
  const refs = useRef<Array<HTMLButtonElement | null>>([])
  const [pill, setPill] = useState<{ left: number; width: number } | null>(null)
  const index = options.findIndex((o) => o.value === current)

  useLayoutEffect(() => {
    const el = refs.current[index]
    if (el) setPill({ left: el.offsetLeft, width: el.offsetWidth })
  }, [index, options.length])

  return (
    <div style={styles.track}>
      {pill && <span style={{ ...styles.pill, left: pill.left, width: pill.width }} aria-hidden />}
      {options.map((o, i) => (
        <button
          key={o.value}
          ref={(el) => {
            refs.current[i] = el
          }}
          style={{ ...styles.cell, ...(i === index ? styles.cellActive : null) }}
          onClick={() => onPick(o.value)}
          aria-pressed={i === index}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,.4)',
    display: 'flex',
    alignItems: 'flex-end',
    zIndex: 25,
  },
  sheet: {
    width: '100%',
    paddingBottom: 'env(safe-area-inset-bottom)',
    borderRadius: '16px 16px 0 0',
    background: '#fafafa',
    color: 'var(--ink)',
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    background: 'var(--ink-divider)',
    margin: '8px auto',
  },
  cards: { display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 16px 16px' },
  card: { borderRadius: 8, background: 'var(--card)', overflow: 'hidden' },
  optionRow: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 12px' },
  actionRow: {
    width: '100%',
    height: 54,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '0 16px 0 12px',
    textAlign: 'left',
    color: 'var(--ink)',
    background: 'transparent',
    borderRadius: 0,
  },
  label: { flex: '0 0 auto', fontSize: 14, whiteSpace: 'nowrap' },
  chevron: { marginLeft: 'auto', display: 'grid', color: 'var(--ink-unavailable)' },
  scroller: { flex: 1, minWidth: 0, marginLeft: 22, overflowX: 'auto', display: 'flex' },
  track: {
    position: 'relative',
    marginLeft: 'auto',
    height: 34,
    padding: 2,
    display: 'flex',
    borderRadius: 8,
    background: 'rgba(0,0,0,.03)',
  },
  pill: {
    position: 'absolute',
    top: 2,
    bottom: 2,
    borderRadius: 6,
    background: 'var(--card)',
    transition: 'left .3s ease-in-out, width .3s ease-in-out',
  },
  cell: {
    position: 'relative',
    minWidth: 37,
    padding: '0 6px',
    fontSize: 12,
    whiteSpace: 'nowrap',
    color: 'var(--ink-watched)',
    background: 'transparent',
    borderRadius: 6,
  },
  cellActive: { color: 'var(--ink)', fontWeight: 700 },
  cancel: {
    width: '100%',
    padding: '16px 0',
    fontSize: 16,
    color: 'var(--ink)',
    background: 'transparent',
    borderTop: '0.5px solid var(--ink-divider)',
    borderRadius: 0,
  },
} satisfies Record<string, React.CSSProperties>
